/**
 * LiquidGlassEMRCourseView — Apple-style "Liquid Glass" rendition of the
 * EMR Sales Toolkit learning path course view.
 *
 * Scope: ONLY used when `course.id === "emr-sales-toolkit"`. Every other
 * course continues to render through the existing `CourseView`. Don't
 * generalise this without a UX review — the visual language was
 * commissioned for this specific path.
 *
 * Design tokens (kept inline so the rest of the app keeps its current
 * theme untouched):
 *   - background : layered translucent panels, 28px backdrop blur
 *   - palette    : monochrome graphite + single Apple-blue accent #0A84FF
 *   - typography : SF Pro fallback chain, generous tracking on uppercase
 *   - motion     : framer-motion stagger + tap-scale on chapter cards
 *
 * Drop-in API parity with CourseView:
 *   { course, onBack, onOpenChapter, completedChapters }
 */
import { motion } from "framer-motion";
import {
  ArrowLeft, ChevronRight, CheckCircle2, Clock, BookOpen,
  Rocket, Phone, Shield, Target, Presentation, BarChart3, Search,
  Calendar, Brain, DollarSign, Globe, Heart, HeartPulse, Building2,
  Scale, Radio, Layers, Flag, Smartphone, Table2, Users, TrendingUp,
} from "lucide-react";

const ICON_MAP = {
  rocket: Rocket, phone: Phone, shield: Shield, target: Target,
  presentation: Presentation, chart: BarChart3, search: Search,
  calendar: Calendar, brain: Brain, metrics: TrendingUp,
  funnel: TrendingUp, dollar: DollarSign, globe: Globe,
  heart: Heart, heartpulse: HeartPulse, building: Building2,
  scale: Scale, broadcast: Radio, layers: Layers, flag: Flag,
  smartphone: Smartphone, table: Table2, users: Users,
};

// Single accent across all chapter accents — Liquid Glass is monochrome.
const ACCENT = "#0A84FF";

export default function LiquidGlassEMRCourseView({ course, onBack, onOpenChapter, completedChapters }) {
  const totalChapters = course.chapters?.length || 0;
  const completedCount = course.chapters?.filter((ch) => completedChapters[ch.id]).length || 0;
  const progressPct = totalChapters > 0 ? Math.round((completedCount / totalChapters) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid={`course-view-${course.id}`}
      style={{
        // Outer ambient surface — soft, slightly off-white with a hint
        // of cool light bleeding from the top. Sets the stage for the
        // glass panels above it.
        background: "radial-gradient(120% 80% at 50% -10%, rgba(10, 132, 255, 0.10) 0%, rgba(10, 132, 255, 0) 55%), linear-gradient(180deg, #F5F5F7 0%, #ECECEF 100%)",
        borderRadius: 20,
        padding: 28,
        minHeight: 600,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', system-ui, sans-serif",
        WebkitFontSmoothing: "antialiased",
      }}
    >
      {/* Back nav */}
      <button
        onClick={onBack}
        data-testid="back-to-courses"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", marginBottom: 24,
          background: "rgba(255, 255, 255, 0.55)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          border: "0.5px solid rgba(255, 255, 255, 0.7)",
          boxShadow: "0 1px 0 rgba(255, 255, 255, 0.6) inset, 0 1px 2px rgba(0, 0, 0, 0.04)",
          borderRadius: 999,
          color: "#1d1d1f", fontSize: 12, fontWeight: 590, letterSpacing: "-0.01em",
          cursor: "pointer", transition: "background 0.18s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.75)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.55)"; }}
      >
        <ArrowLeft style={{ width: 13, height: 13 }} /> Learning Paths
      </button>

      {/* Hero panel — primary glass surface */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
        style={{
          position: "relative",
          padding: "32px 36px",
          marginBottom: 28,
          borderRadius: 24,
          // Layered translucency with a hairline border + ambient inner glow.
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.78) 0%, rgba(255, 255, 255, 0.62) 60%, rgba(245, 245, 247, 0.55) 100%)",
          backdropFilter: "blur(28px) saturate(190%)",
          WebkitBackdropFilter: "blur(28px) saturate(190%)",
          border: "0.5px solid rgba(255, 255, 255, 0.85)",
          boxShadow: "0 1px 0 rgba(255, 255, 255, 0.95) inset, 0 0.5px 0 rgba(255, 255, 255, 0.7) inset 0 -1px, 0 22px 60px -28px rgba(10, 22, 40, 0.18), 0 4px 14px -6px rgba(10, 22, 40, 0.10)",
          overflow: "hidden",
        }}
      >
        {/* Soft spotlight gradient in the top-right */}
        <div
          aria-hidden
          style={{
            position: "absolute", top: -80, right: -80, width: 280, height: 280,
            background: "radial-gradient(circle, rgba(10, 132, 255, 0.18) 0%, rgba(10, 132, 255, 0) 70%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18, minWidth: 0 }}>
            <div
              style={{
                width: 56, height: 56, borderRadius: 16, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, rgba(10, 132, 255, 0.95) 0%, rgba(0, 102, 255, 0.85) 100%)",
                boxShadow: "0 8px 22px -8px rgba(10, 132, 255, 0.55), 0 1px 0 rgba(255, 255, 255, 0.3) inset",
              }}
            >
              {(() => { const Icon = ICON_MAP[course.icon] || BookOpen; return <Icon style={{ width: 26, height: 26, color: "#fff" }} />; })()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 590, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(60, 60, 67, 0.6)", marginBottom: 6 }}>
                Learning Path
              </div>
              <h2 style={{ fontSize: 28, fontWeight: 700, color: "#1d1d1f", letterSpacing: "-0.022em", lineHeight: 1.1, marginBottom: 6 }}>
                {course.title}
              </h2>
              <p style={{ fontSize: 14, color: "rgba(60, 60, 67, 0.78)", lineHeight: 1.45, maxWidth: 540, letterSpacing: "-0.005em" }}>
                {course.description}
              </p>
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 44, fontWeight: 700, color: "#1d1d1f", letterSpacing: "-0.025em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {progressPct}<span style={{ fontSize: 22, color: "rgba(60, 60, 67, 0.5)" }}>%</span>
            </div>
            <div style={{ fontSize: 11, color: "rgba(60, 60, 67, 0.6)", marginTop: 4, fontWeight: 510 }}>
              {completedCount} of {totalChapters} chapters
            </div>
          </div>
        </div>
        {/* Glass progress bar */}
        <div style={{ marginTop: 20, height: 6, borderRadius: 999, background: "rgba(10, 22, 40, 0.08)", overflow: "hidden", position: "relative" }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ delay: 0.25, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            style={{
              height: "100%",
              background: "linear-gradient(90deg, #0A84FF 0%, #4DA2FF 100%)",
              borderRadius: 999,
              boxShadow: "0 0 12px rgba(10, 132, 255, 0.45)",
            }}
          />
        </div>
      </motion.div>

      {/* Chapter list — each chapter is its own mini glass card */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="liquid-glass-chapter-list">
        {course.chapters?.map((ch, idx) => {
          const Icon = ICON_MAP[ch.icon] || BookOpen;
          const isComplete = completedChapters[ch.id];
          return (
            <motion.button
              key={ch.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + idx * 0.035, duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
              whileHover={{ y: -2, transition: { duration: 0.18 } }}
              whileTap={{ scale: 0.985 }}
              onClick={() => onOpenChapter(ch)}
              data-testid={`chapter-card-${ch.id}`}
              style={{
                position: "relative", textAlign: "left",
                display: "flex", alignItems: "center", gap: 18,
                padding: "16px 20px",
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.72) 0%, rgba(255, 255, 255, 0.55) 100%)",
                backdropFilter: "blur(22px) saturate(180%)",
                WebkitBackdropFilter: "blur(22px) saturate(180%)",
                border: "0.5px solid rgba(255, 255, 255, 0.7)",
                borderRadius: 16,
                boxShadow: "0 1px 0 rgba(255, 255, 255, 0.85) inset, 0 1px 3px rgba(10, 22, 40, 0.04), 0 8px 24px -16px rgba(10, 22, 40, 0.10)",
                cursor: "pointer", overflow: "hidden",
                fontFamily: "inherit",
              }}
            >
              {/* Chapter icon disc */}
              <div
                style={{
                  width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: isComplete
                    ? "linear-gradient(135deg, rgba(48, 209, 88, 0.95) 0%, rgba(30, 175, 70, 0.85) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.88) 0%, rgba(245, 245, 247, 0.7) 100%)",
                  border: isComplete ? "none" : "0.5px solid rgba(0, 0, 0, 0.06)",
                  boxShadow: isComplete
                    ? "0 6px 16px -6px rgba(48, 209, 88, 0.5)"
                    : "0 1px 0 rgba(255, 255, 255, 0.95) inset, 0 2px 6px -2px rgba(10, 22, 40, 0.08)",
                }}
              >
                {isComplete
                  ? <CheckCircle2 style={{ width: 20, height: 20, color: "#fff" }} />
                  : <Icon style={{ width: 18, height: 18, color: ACCENT }} />}
              </div>

              {/* Chapter title + metadata */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(60, 60, 67, 0.5)" }}>
                    Chapter {ch.order || idx + 1}
                  </span>
                  {isComplete && (
                    <span
                      style={{
                        fontSize: 9, fontWeight: 700, color: "#15803D",
                        background: "rgba(48, 209, 88, 0.12)",
                        padding: "2px 7px", borderRadius: 999,
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}
                    >Done</span>
                  )}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1d1d1f", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ch.title}
                </div>
              </div>

              {/* Trailing meta */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0, color: "rgba(60, 60, 67, 0.55)" }}>
                <span style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4, fontVariantNumeric: "tabular-nums" }}>
                  <Clock style={{ width: 12, height: 12 }} /> {ch.estimated_time}
                </span>
                <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}>{ch.sections?.length || 0} sections</span>
                <ChevronRight style={{ width: 16, height: 16, color: "rgba(60, 60, 67, 0.4)" }} />
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
